/**
 * RFQ-triage derivations the FRONTEND owns (infra C.3: "the RFQ-split preview is FE").
 *
 * These are pure functions of lines the screen already has, so the preview updates on
 * every supplier chip click with no round-trip. Generating the actual RFQs — numbering
 * them, building the protected Excel, sending the mail — is the backend's job.
 */

import type { PickerOption } from './api/useEnquiryFromMail'
import { IN_HOUSE_SUPPLIER } from './directPricing'
import { withoutInternalAnnotations } from './internalNotes'
import type { EnquiryItem, EnquiryItemPatch, Item, ItemContract, RfqMessage, SendRfqPayload, Supplier, UomConversion } from './types'

/**
 * `SupplierPickerModal`'s bulk-mode candidate list: the whole supplier master, offered by
 * IDENTITY. `EnquiryItem.suppliers[]` must carry `Supplier.code` (the Link value RFQ generation
 * and `items.get_supplier_emails` resolve against) — never the display name, which is only for
 * rendering (task-8b-brief.md).
 *
 * Lives here (a plain `.ts` module), not on the `.tsx` component, so a mock-server integration
 * test can import the real function without pulling React/JSX into a Node-only tsconfig project
 * — see `mock-server/bosun/supplierIdentity.test.ts`.
 */
export function bulkCandidateCodes(suppliers: Supplier[]): string[] {
  return suppliers.map((s) => s.code)
}

/**
 * A safety ceiling on how many supplier addresses the picker resolves at once.
 *
 * NOT a display decision — every supplier the picker shows gets its address looked up, because an
 * address you can only see after ticking a supplier cannot help you decide who to tick. This
 * exists solely so one dialog can never fire an unbounded number of requests: `masters.
 * get_suppliers` caps its own page at 200, so in practice this is never the binding limit.
 *
 * The cost is real and worth stating: `items.get_supplier_emails` takes ONE supplier per call
 * (~0.66s), with no bulk form on either module path — measured 2026-08-29, asked for as `D9`.
 * Addresses therefore fill in progressively, six at a time, and each row says "looking up
 * address…" until its own answer lands. Delete this constant the day `D9` ships.
 */
export const MAX_EMAIL_LOOKUPS = 250

/**
 * Which suppliers' addresses the picker should resolve: every one it is showing, plus the
 * selection.
 *
 * Two earlier versions were both wrong in the same direction. Resolving only the TICKED
 * suppliers showed nothing at all on open, since the bulk dialog opens with nothing ticked.
 * Resolving only when the list was short hid addresses precisely when the list was long enough
 * to need them. The address is what a purchaser chooses BY — a supplier with none cannot be sent
 * an RFQ at all — so it is fetched for everything on screen.
 *
 * The selection leads, so the suppliers already committed to resolve first when the browser is
 * working through a long list six at a time.
 */
export function emailLookupCodes(candidates: string[], selected: string[], cap = MAX_EMAIL_LOOKUPS): string[] {
  const chosen = selected.filter((c) => c !== '')
  return [...new Set([...chosen, ...candidates].filter((c) => c !== ''))].slice(0, cap)
}

export const isRfqLine = (line: EnquiryItem): boolean => line.route === 'rfq'
export const isContractLine = (line: EnquiryItem): boolean => line.route === 'contract'

/**
 * Whether a line can be ticked for a bulk action.
 *
 * ---- Why this no longer requires a matched item (2026-08-11) ----
 *
 * It used to read `isRfqLine(line) && !!line.item_code`, which quietly disabled the bulk action
 * that exists FOR unmatched lines. The table offers two:
 *
 *   "Assign suppliers…"      wants lines that HAVE an item — an RFQ needs one
 *   "Match selected → item…" wants lines that DON'T — matching several free-text lines to one
 *                            master item is the entire feature ("each line keeps its own qty")
 *
 * One rule cannot serve both, and the one we had served the first. So an enquiry of six lines
 * with four unmatched showed checkboxes on the two that needed nothing and none on the four that
 * did — reported 2026-08-11 with a screenshot. The bulk-match dialog was unreachable for its own
 * use case.
 *
 * The rule is now the union — any RFQ-routed line — and each ACTION states its own requirement
 * (see `RfqPage`'s toolbar). Assigning a supplier to a not-yet-matched line is harmless and
 * useful: `buildRfqPlan` still needs BOTH before it will raise anything, so the line simply waits
 * until it is matched, with the supplier already set.
 *
 * Contract-routed lines stay out: they are priced from a blanket order and never go to a
 * supplier, so neither bulk action applies to them.
 */
export const isSelectable = (line: EnquiryItem): boolean => isRfqLine(line)

export interface SupplierGroup {
  supplier: string
  lines: EnquiryItem[]
}

/**
 * One RFQ per supplier. A line assigned to several suppliers appears in EACH of their
 * groups — that is the point: the same item quoted competitively by several suppliers.
 */
export function groupBySupplier(lines: EnquiryItem[]): SupplierGroup[] {
  const groups = new Map<string, EnquiryItem[]>()
  for (const line of lines.filter(isRfqLine)) {
    for (const supplier of line.suppliers) {
      /*
        `IN_HOUSE_SUPPLIER` is a marker, not a `Supplier` docname — it exists so a line supplied
        from our own shelf is distinguishable from one with nobody assigned yet.

        A line priced from stock is `route: 'contract'` and never reaches here. But route it BACK
        to `rfq` and the marker is still in `suppliers`, and `create_rfq` would be called with
        `supplier: "In-house stock"` — a LinkValidationError that fails the whole document for a
        supplier the server has never heard of. Skipped here rather than at the call site, so
        every consumer of the grouping is covered.
      */
      if (supplier === IN_HOUSE_SUPPLIER) continue
      const existing = groups.get(supplier)
      if (existing) existing.push(line)
      else groups.set(supplier, [line])
    }
  }
  return [...groups.entries()]
    .map(([supplier, grouped]) => ({ supplier, lines: grouped }))
    .sort((a, b) => a.supplier.localeCompare(b.supplier))
}

/**
 * The minimum the plan needs about an RFQ that already exists. Deliberately NOT the domain
 * `Rfq`: `toRfqStatus` (`useEnquiries.ts`) collapses Draft to `'sent'`, so `status` cannot answer
 * "has this gone out". `sent_at` is the only honest signal, so it is the only one carried. The
 * executor must build these straight from the `get_rfqs` wire rows, not from mapped `Rfq`
 * objects, so the lossy mapper is never in this path.
 */
export interface ExistingRfq {
  rfq: string
  supplier: string
  sent_at: string | null
  /**
   * The `enquiry_row_no`s on the RFQ — what the supplier was actually asked to quote. Known from
   * `get_rfqs`' items (2026-09-15); ABSENT means unknown, and an unknown sent RFQ is treated as
   * covering every current line, which is the old (safe) behaviour.
   */
  rows?: string[]
}

/** Lines a sent RFQ already carries — reported, never re-sent. */
export interface AlreadySent {
  rfq: string
  lines: EnquiryItem[]
}

export type RfqAction =
  | { kind: 'create'; supplier: string; lines: EnquiryItem[]; alreadySent?: AlreadySent }
  | { kind: 'update'; supplier: string; rfq: string; lines: EnquiryItem[]; alreadySent?: AlreadySent }
  // `lines` here are the lines that were left out — not sent, not carried into any RFQ — so the
  // caller can report "…and N lines were not included" rather than just a bare skip.
  | { kind: 'skip-sent'; supplier: string; rfq: string; lines: EnquiryItem[] }

/**
 * Stopgap for `rfq.generate_rfqs`, dead since 2026-08-05 (`Field company does not exist on Bosun
 * Settings`). Decides, per supplier, whether to `create` a new RFQ, `update` an untouched draft,
 * or `skip-sent` a supplier who was already mailed — never both `update` and re-`create` for the
 * same supplier, which would double-mail them. Delete this (and the rest of this stopgap — see
 * the design doc §6) once `generate_rfqs` works again.
 *
 * Matching is on the `supplier` Link value only, never a display name — `EnquiryItem.suppliers`
 * and `ExistingRfq.supplier` are both identities, and two different suppliers can share a name.
 */
export function buildRfqPlan(lines: EnquiryItem[], existing: readonly ExistingRfq[]): RfqAction[] {
  const plan: RfqAction[] = []
  for (const group of groupBySupplier(lines)) {
    const usable = group.lines.filter((l) => !!l.item_code)
    if (usable.length === 0) continue

    // Sorted by `rfq` name so the update target is a property of the DATA, not of whatever order
    // `get_rfqs` happened to return — with two unsent drafts for one supplier, an unsorted `[0]`
    // pick can differ between a run and its retry, orphaning a draft that still holds items and
    // can still be mailed later.
    const mine = existing.filter((e) => e.supplier === group.supplier).sort((a, b) => a.rfq.localeCompare(b.rfq))
    const sentOnes = mine.filter((e) => typeof e.sent_at === 'string' && e.sent_at.trim() !== '')
    const sent = sentOnes[0]
    /*
      What the supplier already has. A sent RFQ whose rows are unknown covers EVERYTHING — the
      original rule, kept for safety. With rows known (2026-09-15), only the lines on no sent
      RFQ go out, on a fresh supplementary RFQ (or an unsent draft), so an item added after the
      first send still reaches the supplier without the sent sheet ever being rewritten.
    */
    let remaining = usable
    let alreadySent: AlreadySent | undefined
    if (sent) {
      if (sentOnes.some((e) => e.rows === undefined)) {
        plan.push({ kind: 'skip-sent', supplier: group.supplier, rfq: sent.rfq, lines: usable })
        continue
      }
      const sentRows = new Set(sentOnes.flatMap((e) => e.rows ?? []))
      remaining = usable.filter((l) => !sentRows.has(l.cust_sno))
      if (remaining.length === 0) {
        plan.push({ kind: 'skip-sent', supplier: group.supplier, rfq: sent.rfq, lines: [] })
        continue
      }
      alreadySent = { rfq: sent.rfq, lines: usable.filter((l) => sentRows.has(l.cust_sno)) }
    }
    const draft = mine.find((e) => !(typeof e.sent_at === 'string' && e.sent_at.trim() !== ''))
    plan.push(
      draft
        ? { kind: 'update', supplier: group.supplier, rfq: draft.rfq, lines: remaining, ...(alreadySent ? { alreadySent } : {}) }
        : { kind: 'create', supplier: group.supplier, lines: remaining, ...(alreadySent ? { alreadySent } : {}) },
    )
  }
  return plan
}

/**
 * What the purchaser is told after a generate run, as ONE line: what was raised, who was left
 * out because they had already been mailed, and what failed and why.
 *
 * A skip is **information, not an error** — the run did exactly the right thing by not touching an
 * RFQ a supplier already has — but silence about it is not an option either: the purchaser would
 * otherwise be left believing a supplier's revised lines went out when they did not. So it names
 * the supplier AND the number of lines that were left out.
 *
 * Structurally typed rather than importing `GenerateRfqsResult` (`api/useEnquiryMutations.ts`), so
 * this pure module never depends on the mutation layer that depends on it.
 */
export function summarizeRfqGeneration(
  result: {
    raised: number
    skipped: Array<{ supplier: string; lines: number }>
    failed: Array<{ supplier: string; message: string }>
  },
  /** `Supplier.code` → display name. A code with no entry renders as itself. */
  displayName: (code: string) => string = (code) => code,
): string {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
  const parts: string[] = []
  if (result.raised > 0) parts.push(`${plural(result.raised, 'RFQ', 'RFQs')} raised`)
  for (const skip of result.skipped) {
    parts.push(`already sent to ${displayName(skip.supplier)} — ${plural(skip.lines, 'line', 'lines')} not re-sent`)
  }
  for (const failure of result.failed) {
    parts.push(`failed for ${displayName(failure.supplier)} (${failure.message})`)
  }
  // Only reachable when every supplier was skipped or failed — never a silent "done".
  return parts.length > 0 ? parts.join(' · ') : 'No RFQs were raised'
}

export { withoutInternalAnnotations, printedDescription } from './internalNotes'

/** One `RFQ Item` row as `create_rfq`/`update_rfq` accept it (verified live 2026-08-06). */
export interface RfqItemRow {
  item_code: string
  enquiry_row_no: string
  qty: number
  uom: string
  conversion_factor: number
  description: string
}

/**
 * Rows for `create_rfq`/`update_rfq`'s `items` array. A line with no matched `item_code` is
 * dropped — `RFQ Item.item_code` is a Link and would reject it, taking the whole document down.
 *
 * `nameByCode` is the catalogue's own name for each item, used only for a line whose description
 * box is empty (`rfqDescription`). `SendRfqModal` prints the same helper, so the workbook and the
 * preview agree. Optional, because the caller may not hold the map.
 */
/** The line's description only when a purchaser actually changed it from the customer's text. */
export function editedDescription(line: Pick<EnquiryItem, 'description' | 'raw_text'>): string {
  // Compared without our notes: a description that differs only by "(customer's unit: …)" is the
  // customer's own text, not an edit (2026-10-02).
  const d = withoutInternalAnnotations(line.description)
  return d !== '' && d !== withoutInternalAnnotations(line.raw_text) ? d : ''
}

/**
 * What an RFQ line prints for the supplier (Akhil, 2026-11-03: "always use the description, if
 * edited or not edited").
 *
 * The description box, edited or not, with our internal notes removed. It used to give way to
 * the master name whenever it still matched the customer's text, so an unedited line printed the
 * catalogue name. Now the master name is used only when the box is empty, then the customer's
 * words. Never blank: if stripping our notes empties everything, the raw text goes as it stands.
 */
export function rfqDescription(line: Pick<EnquiryItem, 'description' | 'raw_text'>, masterName?: string): string {
  return (
    withoutInternalAnnotations(line.description ?? '') ||
    (masterName ?? '').trim() ||
    withoutInternalAnnotations(line.raw_text) ||
    line.raw_text
  )
}

export function buildRfqItemRows(lines: EnquiryItem[], nameByCode?: Map<string, string>): RfqItemRow[] {
  return lines
    .filter((l) => !!l.item_code)
    .map((l) => ({
      item_code: l.item_code as string,
      // `cust_sno` IS the wire's `enquiry_row_no`. Never `line_id` — that is the child docname,
      // and sending it would break the enquiry → RFQ → SQ → comparison → quotation join chain.
      enquiry_row_no: l.cust_sno,
      qty: l.qty,
      uom: l.uom,
      // Always 1, not because the line is necessarily in its base UOM, but because `EnquiryItem`
      // carries no conversion factor for the caller to send instead.
      conversion_factor: 1,
      // The description box, edited or not — see `rfqDescription`.
      description: rfqDescription(l, nameByCode?.get(l.item_code as string)),
    }))
}

/** RFQ lines that still have nobody to send to — the warning under the split preview. */
export const unassignedRfqLines = (lines: EnquiryItem[]): EnquiryItem[] =>
  lines.filter((l) => isRfqLine(l) && l.suppliers.length === 0)

/**
 * RFQ-routed lines still at quantity 0 — a line the reader could not find a figure for comes
 * through at 0 rather than dropped (Akhil, 2026-09-22), and an RFQ must not ask for 0 of
 * anything. These hold "Generate RFQs & send" until the purchaser fills them in.
 */
export const linesWithoutQty = (lines: EnquiryItem[]): EnquiryItem[] => lines.filter((l) => isRfqLine(l) && !(l.qty > 0))

export interface TriageCounts {
  total: number
  contract: number
  rfq: number
  rfqCount: number
  unassigned: number
}

/** The counts in the banner above the table. */
export function triageCounts(lines: EnquiryItem[]): TriageCounts {
  return {
    total: lines.length,
    contract: lines.filter(isContractLine).length,
    rfq: lines.filter(isRfqLine).length,
    rfqCount: groupBySupplier(lines).length,
    unassigned: unassignedRfqLines(lines).length,
  }
}

/**
 * Indicative unit price for an item: the cheapest standing contract rate, which the API
 * returns as `base_price`. Items without a contract have no price until their supplier
 * quotations arrive, so this is `null` for them rather than a guess.
 */
export function indicativePrice(item: Item | undefined): Item['base_price'] {
  return item?.base_price ?? null
}

/**
 * Layers on-demand item enrichment onto the base item a batch fetch already returned — contracts
 * (`items.get_blanket_orders`) and live stock (`items.get_stock`), fetched separately per item
 * because the catalogue runs to 18,836 rows and neither can be a per-row-on-render fetch (see
 * `LineRow.tsx`'s own comment on when it actually calls them).
 *
 * Each argument is `undefined` until its own query has actually resolved, and `undefined` here
 * means "leave the base item's placeholder alone" — never "overwrite with nothing". `contracts`
 * defaults to `[]` on the base item already, so a still-loading contracts query correctly leaves
 * that `[]` in place rather than asserting "no contracts exist" a query in flight.
 *
 * `on_hand` gets the one genuinely branching rule: it only becomes a number when the stock
 * source itself says `stock_enabled: true`. A `stock_enabled: false` answer must NOT collapse
 * `on_hand` to `0`; it stays `undefined` so the row reads "—" (unknown) rather than "nil" (a
 * fact). The ERP is connected as of 2026-08-29, so this now normally returns real quantities.
 *
 * ---- `on_hand` carries AVAILABLE, not shelf quantity ----
 *
 * Measured: `{"qty": 28.0, "reserved_qty": 33.0}`. Twenty-eight on the shelf with thirty-three
 * already committed is not twenty-eight to sell — it is five SHORT. `exceedsOnHand`
 * (`lib/format.ts`), the "this quantity exceeds stock" warning, reads this field, so filling it
 * with the shelf figure would make that warning lie in exactly the case it exists to catch.
 * `on_shelf` and `reserved` ride alongside as context, so a row can explain the number.
 */
export function mergeItemEnrichment(
  item: Item,
  enrichment: {
    contracts?: ItemContract[]
    stock?: { stock_enabled: boolean; qty: number | null; reserved: number | null; available: number | null }
  },
): Item {
  const stock = enrichment.stock
  if (!stock?.stock_enabled) {
    return { ...item, contracts: enrichment.contracts ?? item.contracts }
  }
  return {
    ...item,
    contracts: enrichment.contracts ?? item.contracts,
    on_hand: stock.available ?? undefined,
    on_shelf: stock.qty ?? undefined,
    reserved: stock.reserved ?? undefined,
  }
}

/**
 * Every UOM the item master accepts, base first at factor 1 — the single source for both
 * UOM pickers and the "available:" text, so they can never disagree.
 *
 * Built defensively: contract §4's example lists `uom_conversions` WITHOUT the base UOM
 * while §6's `available` array includes it at factor 1. Rather than depend on which one a
 * given backend follows, the base is prepended here and any duplicate dropped.
 */
export function uomOptions(item: Item | undefined): UomConversion[] {
  if (!item) return []
  return [
    { uom: item.base_uom, factor: 1 },
    ...item.uom_conversions.filter((c) => c.uom !== item.base_uom),
  ]
}

/**
 * Every accepted UOM as a picker option, each carrying its own conversion — the list the RFQ
 * line's UOM control shows.
 *
 * ---- Why the factor is on the OPTION and not under the control ----
 *
 * The control used to be a bare `<select>`: picking a unit was the only way to learn what it
 * converted to, so comparing three units meant selecting each in turn and reading the line that
 * appeared underneath. The factor belongs where the choice is made. One open of the list now
 * answers it for every unit at once.
 *
 * `qty` is the line's current quantity, so the option can state the ACTUAL result
 * (`2 CASE = 24 TRAY`) rather than a rate the purchaser has to multiply in their head. Pass 0 and
 * the option falls back to the unit rate (`1 CASE = 12 TRAY`).
 *
 * ---- Why a 1:1 unit prints no conversion ----
 *
 * ⚠️ CORRECTION 2026-08-11. This comment previously claimed every live factor was 1.0, measured
 * across 135 items. That measurement was worthless: it queried `items.get_items?search=`, and
 * this method's search parameter is **`q`** — `search` is silently ignored, so all twelve
 * "different" searches returned the same unfiltered first page, and the sample was one item
 * repeated. Re-measured with `q`, over 234 DISTINCT items and 276 conversion rows:
 *
 *     factor 1.0 ....... 240 rows
 *     real factors ......  36 rows — 2, 3, 4, 5, 6, 10, 12, 15, 24, 32, 48, 50, 72, 96, 144
 *     e.g. LAUNDRY SOAP POWDER TIDE 1.5KG → CASE = 6
 *          FRESH EGG WHITE 30 S x 12 TRAY → CASE = 12
 *
 * So real conversions are a live, present-day fact, not something to wait for. Most rows are
 * still 1:1 (a unit the item can be ordered in that happens not to repackage), and printing
 * `1 SET = 1.0 Nos` for each of those is noise that buries the handful that mean something —
 * which is why the sublabel appears only where the factor is real. The behaviour is unchanged by
 * the correction; only the reason it is right.
 */
export function uomComboOptions(item: Item | undefined, qty = 0, customerUom?: string | null): PickerOption[] {
  if (!item) return []
  const base = item.base_uom
  const asked = (customerUom ?? '').trim().toLowerCase()

  const options = uomOptions(item).map((c) => {
    const isBase = c.uom === base
    // Point 12: the unit the CUSTOMER asked in, where the item's master happens to define it.
    // This is the whole "choose the relevant uom accordingly" step — when the two do line up, the
    // right answer should be one recognisable click rather than a search through the list.
    const isAsked = asked !== '' && c.uom.trim().toLowerCase() === asked
    // A base unit converts to itself; saying so is noise. So is `= 1.0` on a 1:1 conversion.
    const sublabel =
      isBase || c.factor === 1
        ? undefined
        : qty > 0
          ? `${qty} ${c.uom} = ${round2(qty * c.factor)} ${base}`
          : `1 ${c.uom} = ${c.factor} ${base}`
    return {
      name: c.uom,
      label: c.uom,
      sublabel,
      // What the customer asked for outranks "base unit" as a note: the base is a property of the
      // catalogue, this is the reason the purchaser is looking at the list at all.
      note: isAsked ? 'customer asked in this' : isBase ? 'base unit' : undefined,
      isAsked,
    }
  })

  // The customer's own unit first where the master has it — the purchaser is translating FROM it.
  return options.sort((a, b) => Number(b.isAsked) - Number(a.isAsked)).map(({ isAsked: _drop, ...o }) => o)
}

/** Conversions can be fractional (`0.001 KG`); a float artefact in a picker reads as a data bug. */
const round2 = (n: number): number => Math.round(n * 100) / 100

/** "pcs (base), box=24, carton=288" — shown when a line's UOM isn't in the master. */
export function uomAvailabilityText(item: Item): string {
  return uomOptions(item)
    .map((c) => (c.factor === 1 ? `${c.uom} (base)` : `${c.uom}=${c.factor}`))
    .join(', ')
}

/* ============ the outgoing RFQ mail (contract §5) ============ */

/**
 * One supplier's RFQ as it will be sent: who it goes to, what it says, and exactly which
 * lines they are being asked to price.
 *
 * The purchaser reviews and edits `to`, `cc`, `subject` and `note`. `lines` is not editable —
 * it comes from triage, and the protected Excel is generated from the same record, so the
 * table in the mail and the sheet the supplier fills in cannot disagree.
 */
export interface RfqDraft {
  /** `Supplier.code` — identity: matches `EnquiryItem.suppliers[]`/RFQ ids/`onChange` keys. */
  supplier: string
  /** Display name only, resolved once when the draft is built — render this, never `supplier`. */
  supplierName: string
  email: string
  /**
   * Addresses as TYPED — comma-separated text, not a parsed array.
   *
   * Parsing on every keystroke and rendering the result back into the input eats the
   * separator the moment it is typed, so a second address can never be entered. The split
   * happens once, on send.
   */
  to: string
  cc: string
  subject: string
  note: string
  /** The lines that will actually go out — for a supplier sent to before, only the new ones. */
  lines: EnquiryItem[]
  /** What this supplier already has on a sent RFQ — shown, never re-sent. */
  alreadySent?: AlreadySent
}

/** `list` with repeats and anything in `taken` removed — first spelling wins, compared case-insensitively. */
export function dedupeAddresses(list: readonly string[], taken: readonly string[] = []): string[] {
  const seen = new Set(taken.map((a) => a.trim().toLowerCase()))
  const out: string[] = []
  for (const a of list) {
    const key = a.trim().toLowerCase()
    if (key === '' || seen.has(key)) continue
    seen.add(key)
    out.push(a.trim())
  }
  return out
}

/** The addresses in a typed field, empties dropped. */
export const parseAddresses = (value: string): string[] =>
  value
    .split(/[,;]/)
    .map((a) => a.trim())
    .filter(Boolean)

/** The mailbox RFQs are sent from — the supplier account, matching contract §11. */
export const RFQ_FROM = 'procurement@bosun.app'

/**
 * The default covering note. Deliberately complete enough to send unread: a purchaser who
 * has nothing to add should not have to write anything, which is what makes "Send all" a
 * one-click action rather than a form to fill in N times.
 */
export function defaultRfqNote(_header: { customer: string; vessel: string | null; delivery_port: string | null }): string {
  // Neither the vessel nor the delivery location is named (Akhil, 2026-09-14): a supplier is
  // asked for rates against the sheet, and where the goods are going is the customer's business.
  // The header stays in the signature so the callers need not change if that decision does.
  return [
    'Dear Sirs,',
    '',
    // "in the attached sheet", not "listed below" (2026-10-16): the note must not point at the
    // table the server prints under it — the sheet is the document we want priced (E23).
    'Please quote your best rates and availability for the items in the attached sheet.',
    'Rates should be per the UOM stated. Kindly confirm lead time and stock position against each line.',
    '',
    'The attached sheet has a unit-price column for your reply — please return it completed.',
    // The server appends an item table under this note (`send_rfq`, see `E23` in the asks file)
    // and suppliers have been typing prices into THAT and never opening the workbook (Akhil,
    // 2026-10-16). Until the table goes, the note says so in as many words.
    'Please enter your prices in the attached sheet only — prices typed into this email cannot be processed.',
  ].join('\n')
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * The note with every greeting line removed — `rfq.send_rfq` opens the mail with
 * "Dear <supplier>," itself.
 *
 * RFQ-2026-00081 as received (Akhil, 2026-09-16) read "Dear Shiby, / Request for Quotation /
 * Shiby, / Dear Sirs, / Please quote…": the server's greeting, then the Email Template's
 * `{{ supplier_name }},` line, then `defaultRfqNote`'s own. Only a LEADING "Dear …" used to be
 * dropped, and the template's title line put ours third. So: every "Dear …" line goes, wherever
 * it is, and — when the supplier's name is known — so does a line that is only that name with an
 * optional title ("Shiby,", "Mr Shiby,"). A sign-off ("Regards,") is not a greeting and stays;
 * neither does a sentence that merely mentions the name.
 */
export function withoutGreetings(note: string, supplierName?: string): string {
  const name = supplierName?.trim()
  const bare = name ? new RegExp(`^\\s*(?:(?:mr|mrs|ms|dr|m\\/s)\\.?\\s+)?${escapeRe(name)}\\s*,?\\s*$`, 'i') : null
  const lines = note.split('\n').filter((line) => !/^\s*dear\b/i.test(line) && !(bare && bare.test(line)))
  // A removed greeting leaves its blank line behind; runs of blank lines collapse to one.
  const out: string[] = []
  for (const line of lines) {
    if (line.trim() === '' && (out.length === 0 || out[out.length - 1].trim() === '')) continue
    out.push(line)
  }
  return out.join('\n').trim()
}

/** Resolved addresses for one supplier, from `items.get_supplier_emails` — see that hook's own
 * comment (`useItems.ts`) for why this is the only source: `Supplier.primary_email` is always
 * `''` live (defect B3). */
export interface SupplierEmailInfo {
  to: string[]
  cc: string[]
}

/**
 * Build a draft per supplier from the current triage.
 *
 * The subject carries the `[REQ-####]` tag because that tag is what threads the supplier's
 * reply back to this requirement (§11) — lose it and the reply arrives orphaned.
 */
export function buildRfqDrafts(
  lines: EnquiryItem[],
  enquiryId: string,
  header: { customer: string; vessel: string | null; delivery_port: string | null; purchaser: string },
  emailBySupplier: Map<string, SupplierEmailInfo>,
  /** `Supplier.code` -> display name, for `RfqDraft.supplierName` only. A code with no entry
   * (unknown supplier) renders as itself rather than blanking the draft. */
  supplierName: (code: string) => string = (code) => code,
  /**
   * The server's `Rfq` Email Template, already rendered to plain text for ONE supplier
   * (`renderEmailTemplate`). Preferred over `defaultRfqNote` when present, because it carries the
   * business's own rules — the workbook must come back unmodified, any other format is rejected —
   * which our note never stated. A function of the supplier, not a single string, because the
   * template greets them by name.
   *
   * Falls back to `defaultRfqNote` when the bench has no template, so a send never depends on it.
   */
  templateNote?: (supplierCode: string, supplierDisplayName: string) => string | null,
  /**
   * The RFQs that already exist. With these, a draft shows EXACTLY what `buildRfqPlan` will send:
   * a supplier sent to before gets only the lines their sent RFQ does not carry (Akhil,
   * 2026-09-15 — an item added after the first send), and no draft at all when there are none.
   */
  existing: readonly ExistingRfq[] = [],
): RfqDraft[] {
  const fallback = defaultRfqNote(header)
  const plan = buildRfqPlan(lines, existing)

  return groupBySupplier(lines).flatMap((group) => {
    const action = plan.find((a) => a.supplier === group.supplier)
    // Skipped by the plan (everything already with the supplier), or nothing usable: no draft.
    if (!action || action.kind === 'skip-sent') return []
    const display = supplierName(group.supplier)
    const note = templateNote?.(group.supplier, display) || fallback
    const resolved = emailBySupplier.get(group.supplier)
    const to = resolved?.to.join(', ') ?? ''
    /*
      The purchaser is copied on every RFQ (Akhil, 2026-09-14) so the supplier's reply-all lands
      with the person running the enquiry. Appended after the supplier's own cc, once — a
      supplier record that already lists them is not a reason to mail them twice. `purchaser`
      is a `User` name, which on this bench IS the email address.
    */
    const purchaser = header.purchaser.trim()
    // Nobody twice, and nobody on cc who is already on To (ENQ-2026-00088, 2026-09-16: the
    // purchaser was the supplier's own address, and the server queues one message per recipient,
    // so To + cc on one address was two mails). Case-insensitive, as mail addresses are.
    const cc = dedupeAddresses(purchaser !== '' ? [...(resolved?.cc ?? []), purchaser] : (resolved?.cc ?? []), resolved?.to ?? []).join(', ')
    const draft: RfqDraft = {
      supplier: group.supplier,
      supplierName: supplierName(group.supplier),
      email: to,
      // An unknown address leaves `to` empty rather than inventing one: the dialog refuses to
      // send it, which is the honest outcome — better than mailing nowhere silently.
      to,
      cc,
      // No vessel here either (5e) — `[${enquiryId}]` is what threads the reply, and it stays.
      subject: `[${enquiryId}] RFQ — ${action.lines.length} item${action.lines.length === 1 ? '' : 's'}`,
      note,
      lines: action.lines,
      ...(action.alreadySent ? { alreadySent: action.alreadySent } : {}),
    }
    return [draft]
  })
}

/** The drafts a send would reject, so the dialog can say so before the click. */
export const draftsWithoutRecipient = (drafts: RfqDraft[]): RfqDraft[] =>
  drafts.filter((d) => parseAddresses(d.to).length === 0)

/**
 * After a supplier sync (2026-10-26): the drafts that had NO address and now have one. A draft with
 * an address — resolved before, or typed — is the purchaser's and is left alone.
 */
export function recipientFills(drafts: readonly RfqDraft[], found: ReadonlyMap<string, readonly string[]>): { supplier: string; to: string }[] {
  return drafts
    .filter((d) => parseAddresses(d.to).length === 0)
    .flatMap((d) => {
      const addresses = found.get(d.supplier) ?? []
      return addresses.length > 0 ? [{ supplier: d.supplier, to: addresses.join(', ') }] : []
    })
}

/** A draft as the API takes it (contract §5) — the one place the typed text becomes a list. */
export const toRfqMessage = (draft: RfqDraft): RfqMessage => {
  // Typed by hand, so deduplicated HERE too: one message per recipient on the server means a
  // repeated address is a repeated mail (ENQ-2026-00088).
  const to = dedupeAddresses(parseAddresses(draft.to))
  return {
    supplier: draft.supplier,
    to,
    cc: dedupeAddresses(parseAddresses(draft.cc), to),
    subject: draft.subject,
    note: draft.note,
  }
}

/* ============ generate-then-send, retry-safe (Task 8 review, IMPORTANT) ============ */

export interface SendDraftsResult {
  /** Every supplier this call now knows an RFQ id for — this round's PLUS whatever the caller
   * already had. The caller feeds this back in on a retry so a supplier who already has an RFQ
   * is never re-generated for. */
  rfqIdBySupplier: Map<string, string>
  /** Suppliers whose `send_rfq` succeeded THIS round. */
  sentSuppliers: string[]
  /** Suppliers whose `send_rfq` failed THIS round, named, so the caller can report each one and
   * retry only these. */
  failures: Array<{ supplier: string; message: string }>
  /** Suppliers the generate step deliberately left alone (their RFQ was already sent). Their
   * drafts were pruned before the send loop, so they appear in NEITHER `sentSuppliers` nor
   * `failures`. Reported here so the caller can drop them from the drafts it holds — the skip has
   * already been described accurately, once, by the generate step's own summary. */
  skippedSuppliers: string[]
}

/**
 * What the `generate` callback hands back. It is not just the id map, because a supplier can come
 * out of a generate run in three states, and the third one used to be indistinguishable from a
 * failure:
 *
 * - an RFQ was raised → it has an id, send it;
 * - the write failed → no id, and NOT sending is the right answer (`sendRfqDrafts` throws for it);
 * - it was **correctly skipped** — the supplier's RFQ was already sent, and rewriting it would
 *   change what they were asked to quote. Also no id, but nothing went wrong, and telling the
 *   purchaser "no RFQ was generated for it" is simply false.
 */
export interface GeneratedRfqIds {
  /** supplier → the RFQ id raised for them this round. */
  rfqIdBySupplier: Map<string, string>
  /** Suppliers left alone because their RFQ was already sent. Never given an id: handing one to
   * the sender is how a supplier gets mailed the same RFQ twice. */
  skipped: string[]
}

/**
 * Sends every draft, generating RFQs first ONLY when at least one draft's supplier has no known
 * RFQ id yet — a retry after a partial failure must reuse the ids it already has, never blindly
 * re-run `generate_rfqs`. Every draft is sent independently (`Promise.allSettled`), so one
 * supplier's transient failure never blocks the others.
 *
 * This does NOT decide which drafts to retry — that is the caller's job (drop `sentSuppliers`
 * from the next call's `drafts`, keep `rfqIdBySupplier`), because a retry after a partial failure
 * must send ONLY what did not already go out: re-sending a supplier who already received their
 * RFQ mails them twice for the same enquiry, which is the bug this function exists to prevent.
 * `RfqPage.tsx`'s `sendAllDrafts` is the caller; this is factored out as a pure function (no
 * React, no `mutate` calls) so the retry behaviour can be unit-tested directly.
 *
 * **Skipped suppliers are PRUNED, not failed** (review round 2, IMPORTANT). A supplier whose RFQ
 * was already sent is deliberately given no id by `generate` — handing one to the sender is how
 * they get mailed twice. But "no id" also means "the write failed", and the throw below then
 * produced a second, false toast about a supplier who was correctly left alone
 * (`"already sent to Top Go FZE — 4 lines not re-sent"` immediately followed by
 * `"Top Go FZE: no RFQ was generated for it"`), with no re-send affordance, so retrying reproduced
 * it forever. `generate` now names the skips, and their drafts never reach the send loop — so the
 * skip is reported once, accurately, by the generate summary alone. The "no id means don't send"
 * rule is untouched for every OTHER supplier: a genuinely failed write must still be named.
 */
export async function sendRfqDrafts(
  drafts: RfqDraft[],
  knownRfqIds: Map<string, string>,
  generate: () => Promise<GeneratedRfqIds>,
  send: (payload: SendRfqPayload) => Promise<unknown>,
  /**
   * RFQ ids the SERVER now records as sent — re-read at the moment of a retry.
   *
   * Required, not optional: a default of "assume nothing was sent" is exactly the bug this
   * parameter closes, and it would be reintroduced by any caller who forgot to pass it.
   */
  alreadySent: () => Promise<Set<string>>,
): Promise<SendDraftsResult> {
  let rfqIdBySupplier = knownRfqIds
  let sendable = drafts
  let skippedSuppliers: string[] = []
  const needsGenerate = drafts.some((d) => !rfqIdBySupplier.has(d.supplier))
  if (needsGenerate) {
    const generated = await generate()
    rfqIdBySupplier = new Map([...rfqIdBySupplier, ...generated.rfqIdBySupplier])
    // Only the skips this round's generate actually reported — never inferred from a missing id.
    skippedSuppliers = generated.skipped.filter((s) => drafts.some((d) => d.supplier === s))
    if (skippedSuppliers.length > 0) {
      const skipped = new Set(skippedSuppliers)
      sendable = drafts.filter((d) => !skipped.has(d.supplier))
    }
  } else {
    /*
      THE RETRY PATH, and the one that could mail a supplier twice.

      `generate()` applies the skip-sent rule (`buildRfqPlan` reads each RFQ's `sent_at`). On a
      retry every draft already has an id — generation succeeded, only the send failed — so
      `needsGenerate` is false, `generate()` never runs, and that rule never executes. The
      ambiguous case is the dangerous one: the server mailed the supplier and stamped `sent_at`,
      but we never saw the confirmation (a timeout, a 502, a non-`success` body returned after the
      mail went). We recorded a failure, kept the draft, and the retry mails them again.

      So the server is asked directly, at the moment of the retry, which RFQs it now considers
      sent. Anything in that set is pruned and reported as skipped rather than mailed.

      This deliberately does NOT catch its own failure. If we cannot establish whether an RFQ has
      gone out, the honest move is to send nothing and say so — the alternative is mailing a
      customer's supplier a second time, which cannot be taken back. Same fail-closed rule
      `readRfqList` follows for the same reason.
    */
    const sent = await alreadySent()
    const already = sendable.filter((d) => {
      const rfqId = rfqIdBySupplier.get(d.supplier)
      return rfqId !== undefined && sent.has(rfqId)
    })
    if (already.length > 0) {
      skippedSuppliers = already.map((d) => d.supplier)
      const skipped = new Set(skippedSuppliers)
      sendable = sendable.filter((d) => !skipped.has(d.supplier))
    }
  }

  const outcomes = await Promise.allSettled(
    sendable.map(async (draft) => {
      const rfqId = rfqIdBySupplier.get(draft.supplier)
      if (!rfqId) throw new Error(`${draft.supplierName}: no RFQ was generated for it`)
      const message = toRfqMessage(draft)
      try {
        await send({ rfq: rfqId, to: message.to, cc: message.cc, subject: message.subject, note: message.note })
      } catch (error) {
        // Named per supplier — the one place that still has the SUPPLIER to attach to a failure
        // whose own message (e.g. `useSendRfq`'s empty-`to` guard) only carries the RFQ id. The
        // NAME, not the code: this string is only ever shown to the purchaser (RfqPage.tsx).
        const reason = error instanceof Error ? error.message : String(error)
        throw new Error(`${draft.supplierName}: ${reason}`)
      }
      return draft.supplier
    }),
  )

  const sentSuppliers: string[] = []
  const failures: Array<{ supplier: string; message: string }> = []
  outcomes.forEach((outcome, i) => {
    if (outcome.status === 'fulfilled') sentSuppliers.push(outcome.value)
    // Indexed against `sendable`, the array actually mapped over — `drafts[i]` would attribute a
    // failure to the wrong supplier once anything has been pruned.
    else failures.push({ supplier: sendable[i].supplier, message: (outcome.reason as Error).message })
  })

  return { rfqIdBySupplier, sentSuppliers, failures, skippedSuppliers }
}

/* ============================================================================================
 * Reminder drafts — chasing a supplier who hasn't answered
 * ============================================================================================ */

/**
 * How long an RFQ has been outstanding, in whole days, or `null` when we don't know.
 *
 * Reads the wire's `"YYYY-MM-DD HH:mm:ss"` **site-local** datetime (contract §1 — NOT ISO-8601
 * UTC, so it must not be handed to `new Date(...)` as-is on a browser in another timezone without
 * this normalisation). Returns `null` rather than 0 for an unparseable or absent value: "sent 0
 * days ago" is a claim, and we would be making it up.
 */
export function daysAwaiting(sentAt: string | null | undefined, now: Date): number | null {
  if (typeof sentAt !== 'string' || sentAt.trim() === '') return null
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(sentAt.trim())
  if (!m) return null
  const [, y, mo, d, h, mi, sec] = m
  const sent = new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(sec))
  if (Number.isNaN(sent.getTime())) return null
  const days = Math.floor((now.getTime() - sent.getTime()) / 86_400_000)
  // A future timestamp (clock skew between our machine and the bench) is not a negative age.
  return days < 0 ? 0 : days
}

/** Human phrasing for the age, for use inside a sentence. */
export function describeAwaiting(days: number | null): string {
  if (days === null) return 'some time ago'
  if (days === 0) return 'earlier today'
  if (days === 1) return 'yesterday'
  return `${days} days ago`
}

export interface ReminderDraftInput {
  rfq: string
  enquiryId: string
  supplierName: string
  /** From `items.get_supplier_emails` — never `Supplier.primary_email`, which is always blank. */
  to: string[]
  cc?: string[]
  /** The RFQ's `sent_at` from the wire. */
  sentAt: string | null | undefined
  /** Optional `YYYY-MM-DD` the purchaser wants a reply by. Blank by default — inventing a
   *  deadline on a supplier's behalf is a commercial statement, not a UI default. */
  replyBy?: string
  now: Date
}

/**
 * Composes the chase mail a purchaser reviews before sending.
 *
 * The subject keeps the `[<enquiry-id>]` tag, because that tag is what threads a supplier's reply
 * back to this requirement (`buildRfqDrafts` relies on the same thing) — a reminder that loses it
 * produces a reply nobody can match. It is additionally prefixed `Reminder — ` so the supplier can
 * see at a glance that this is a chase, not a second, different RFQ.
 *
 * The body deliberately does NOT restate the item lines. The server re-attaches the SAME workbook
 * (`type: 'reminder'` on `send_rfq_email_with_excel`), so a restated table in the body could drift
 * from the attachment if the RFQ were edited between sends — and the supplier would have two
 * disagreeing versions of what they are being asked to price. One source, the attachment.
 */
export function buildReminderDraft(input: ReminderDraftInput): RfqMessage {
  const age = describeAwaiting(daysAwaiting(input.sentAt, input.now))
  const by = (input.replyBy ?? '').trim()

  // PLAIN TEXT, not HTML — the purchaser edits this in a textarea, and asking someone to hand-edit
  // `<p>` tags to reword a sentence is how a malformed mail reaches a supplier. `textToHtml`
  // converts it on the way out, the same treatment `defaultRfqNote` gets.
  return {
    supplier: input.supplierName,
    to: input.to,
    cc: input.cc ?? [],
    // The server's own first-send subject with "Reminder:" in front — its `[REQ-RFQ-…]` tag is
    // what threads the reply (2026-09-16); the enquiry id alone did not.
    subject: `Reminder: Request for Quotation — ${input.rfq} [REQ-${input.rfq}]`,
    note: [
      `Dear ${input.supplierName},`,
      '',
      `We sent you Request for Quotation ${input.rfq} ${age} and have not yet received your quotation.`,
      '',
      'The workbook is attached again for your convenience. Please revert with your best rate, ' +
        'available quantity, unit of measure and delivery lead time.',
      ...(by ? ['', `We would be grateful for your response by ${by}.`] : []),
      '',
      'If you are unable to quote on this occasion, a short note to that effect would help us plan.',
      '',
      'Thank you,',
      'Procurement',
    ].join('\n'),
  }
}

/**
 * Plain text → the minimal HTML an email body needs.
 *
 * Both note builders (`defaultRfqNote` and `buildReminderDraft`) produce plain text, because that
 * is what a purchaser can safely edit. `send_rfq_email_with_excel` renders `message` as HTML, so a
 * plain-text note sent as-is arrives as one unbroken paragraph with every line run together.
 *
 * Escapes first, then formats: a supplier name or a purchaser's own words containing `&` or `<`
 * must not become markup, and must not be able to inject any.
 */
export function textToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter((para) => para !== '')
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/**
 * Which RFQ, if any, each enquiry row has already gone out on — keyed by `enquiry_row_no`.
 *
 * A line on a SENT RFQ is being priced by a supplier right now; deleting it from the enquiry
 * would leave their quote pointing at nothing (Akhil, 2026-09-15). The RFQ page disables the
 * delete for such lines and says which RFQ holds them. Unsent drafts do not count — nobody has
 * seen those.
 */
export function sentRfqByRow(
  rfqs: readonly { id: string; sent_at?: string | null; items?: readonly { enquiry_row_no: string }[] }[] | undefined,
): Map<string, string> {
  const out = new Map<string, string>()
  for (const rfq of rfqs ?? []) {
    if (typeof rfq.sent_at !== 'string' || rfq.sent_at.trim() === '') continue
    for (const item of rfq.items ?? []) {
      if (item.enquiry_row_no !== '' && !out.has(item.enquiry_row_no)) out.set(item.enquiry_row_no, rfq.id)
    }
  }
  return out
}

/**
 * The line that already carries `itemCode`, or `null`.
 *
 * Until 2026-10-16 an ADD of such an item was refused outright (Akhil, 2026-09-15: the same item
 * on two lines goes out twice and is quoted twice). Akhil's later ruling: a second line of one
 * item is a VARIANT — paint in another colour, a spare in another size, "medicine" twice — and
 * waiting for the catalogue to grow a variant is not an option. So the add dialog now names the
 * line that has it and asks, and the upload path still skips repeats (a list that names one code
 * twice is far more often a copy-paste than a variant). Matching two different customer lines
 * to one item was always allowed: `MatchLinesPayload` exists for it.
 */
export function existingLineForItem<T extends Pick<EnquiryItem, 'cust_sno' | 'item_code'>>(lines: readonly T[], itemCode: string): T | null {
  const code = itemCode.trim()
  if (code === '') return null
  return lines.find((l) => (l.item_code ?? '').trim() === code) ?? null
}

/**
 * What a line's pricing becomes when its ITEM changes (Akhil, 2026-09-16).
 *
 * A price belongs to an item. When the item is swapped, the old contract rate, stock price or
 * past-supplier price on the line is the OLD item's and cannot stand — so the line is re-routed
 * from the new item's own facts: its active contract on a Provision enquiry (route `contract`,
 * the contracted rate, the contract's supplier), else back to RFQ at no price with the new
 * item's suppliers — even an empty list, since the old item's supplier must go. Re-confirming
 * the same item changes nothing.
 */
export function pricingAfterItemChange(input: {
  changed: boolean
  provision: boolean
  contracts: readonly ItemContract[]
  suppliers: readonly string[]
}): Partial<Pick<EnquiryItemPatch, 'route' | 'selected_rate' | 'suppliers'>> {
  if (!input.changed) return {}
  const contract = input.provision ? input.contracts[0] : undefined
  if (contract) return { route: 'contract', selected_rate: contract.rate.amount, suppliers: [contract.supplier] }
  return { route: 'rfq', selected_rate: 0, suppliers: [...input.suppliers] }
}
